import {
  addTotals,
  createEmptyMicros,
  createEmptyTotals,
  createNutritionItem,
  getFiniteNumber,
} from "./NutritionItems";
import {
  CLIENT_NUTRITION_DEFAULT_PURPOSE,
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "./nutritionPaths";
import {
  deriveRecipeClassification,
  getRuntimeFoodClassification,
  normalizeFoodClassification,
} from "../../nutrition/foods/foodClassification";
import {
  normalizeBuiltInCommonMeasureId,
} from "../../nutrition/shared/commonMeasures";
import {
  assertNutritionLimit,
  validatePlanDraftLimits,
} from "../../nutrition/nutritionLimits";
import {
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  normalizeCatalogFoodSource,
} from "../../nutrition/foods/supabaseFoodCatalogShared";

const NUTRITION_MICRO_KEYS = Object.keys(createEmptyMicros());

const FOOD_SCHEMA_VERSION = "nutrition_food_v1";
const RECIPE_SCHEMA_VERSION = "nutrition_recipe_v1";
const FOOD_MACRONUTRIENT_IDS = Object.freeze([
  "calories",
  "carbs",
  "protein",
  "fats",
  "fiber",
]);
const FOOD_NUTRIENT_ID_SET = new Set([
  ...FOOD_MACRONUTRIENT_IDS,
  ...NUTRITION_MICRO_KEYS,
]);
const RECIPE_MEAL_TYPE_BY_TYPE_KEY = Object.freeze({
  "main-string": "main",
  "lunch-string": "main",
  "dinner-string": "main",
  "breakfast-string": "breakfast",
  "snack-string": "snack",
});
const RECIPE_TYPE_KEY_BY_MEAL_TYPE = Object.freeze({
  main: "main-string",
  breakfast: "breakfast-string",
  snack: "snack-string",
});
const RECIPE_ALLOWED_MEAL_TYPES = new Set(Object.keys(RECIPE_TYPE_KEY_BY_MEAL_TYPE));
const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

export const CLIENT_EDITABLE_PLAN_SCHEMA_VERSION = "nutrition_client_plan_v2";
export const CLIENT_EDITABLE_PLAN_RECORD_KIND = "editable_client_plan";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

export function normalizeNutritionWebsiteUrl(value) {
  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) {
    return "";
  }

  const candidate = URL_SCHEME_PATTERN.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const parsedUrl = new URL(candidate);
    const isSupportedProtocol =
      parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    const hasValidHostname =
      parsedUrl.hostname === "localhost" || parsedUrl.hostname.includes(".");

    if (!isSupportedProtocol || !hasValidHostname) {
      return "";
    }

    return candidate;
  } catch {
    return "";
  }
}

function normalizeDocId(value, fallback = "") {
  const resolvedValue = String(value ?? fallback).trim();
  return resolvedValue.length > 0 ? resolvedValue.toLowerCase() : String(fallback || "");
}

function normalizePlanId(value, fallback = "plan") {
  return normalizeDocId(value, fallback);
}

function isCatalogRuntimeSource(value) {
  const normalizedValue = String(value ?? "").trim().toUpperCase();

  return (
    normalizedValue === CATALOG_FOOD_SOURCE_EFORT ||
    normalizedValue === CATALOG_FOOD_SOURCE_OPEN_SOURCE ||
    normalizedValue === "BEDCA"
  );
}

function normalizeFoodSource(foodLike) {
  if (
    foodLike?._catalog === true ||
    foodLike?.origin?.catalog_food_id ||
    foodLike?.catalogFoodId ||
    isCatalogRuntimeSource(foodLike?.source) ||
    isCatalogRuntimeSource(foodLike?.catalogSource)
  ) {
    return getPersistedCatalogSource(foodLike?.source ?? foodLike?.catalogSource);
  }

  return "coach_created";
}

function getPersistedCatalogSource(value) {
  return normalizeCatalogFoodSource(value, {
    defaultSource: CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  });
}

function getCatalogSourceLabelFromSnapshot(value) {
  return getPersistedCatalogSource(value) === CATALOG_FOOD_SOURCE_EFORT
    ? "EFORT"
    : "OPEN SOURCE";
}

function normalizeIngredientSource(ingredientLike) {
  if (
    ingredientLike?._catalog === true ||
    ingredientLike?.origin?.catalog_food_id ||
    ingredientLike?.catalogFoodId ||
    isCatalogRuntimeSource(ingredientLike?.source) ||
    isCatalogRuntimeSource(ingredientLike?.catalogSource)
  ) {
    return getPersistedCatalogSource(
      ingredientLike?.source ?? ingredientLike?.catalogSource,
    );
  }

  if (ingredientLike?.source === "demo") {
    return "demo";
  }

  if (ingredientLike?.source === "legacy_snapshot") {
    return "legacy_snapshot";
  }

  return "coach_created";
}

function normalizeMicros(source) {
  const resolvedSource = source && typeof source === "object" ? source : createEmptyMicros();
  const normalizedMicros = createEmptyMicros();

  NUTRITION_MICRO_KEYS.forEach((key) => {
    normalizedMicros[key] = getFiniteNumber(resolvedSource[key], 0);
  });

  return normalizedMicros;
}

function normalizeCommonMeasures(commonMeasures) {
  if (!Array.isArray(commonMeasures)) {
    return [];
  }

  const seenIds = new Set();

  return commonMeasures
    .map((measure) => {
      const measureId = normalizeBuiltInCommonMeasureId(
        measure?.measureKey ?? measure?.id,
      );
      const grams = getFiniteNumber(measure?.grams, 0);

      if (!measureId || grams <= 0 || seenIds.has(measureId)) {
        return null;
      }

      seenIds.add(measureId);

      return {
        id: measureId,
        measureKey: measureId,
        grams,
      };
    })
    .filter(Boolean);
}

function normalizeFoodNutrientsPer100g(nutrientsPer100g) {
  if (!Array.isArray(nutrientsPer100g)) {
    return [];
  }

  const seenIds = new Set();

  return nutrientsPer100g
    .map((entry) => {
      const nutrientId = String(entry?.id ?? "").trim();
      const value = getFiniteNumber(entry?.value, 0);

      if (!FOOD_NUTRIENT_ID_SET.has(nutrientId) || seenIds.has(nutrientId)) {
        return null;
      }

      seenIds.add(nutrientId);

      return {
        id: nutrientId,
        value,
      };
    })
    .filter(Boolean);
}

function buildFoodNutrientsPer100g(foodLike) {
  const directNutrients = normalizeFoodNutrientsPer100g(
    foodLike?.nutrientsPer100g ?? foodLike?.nutrients_per_100g,
  ).filter((entry) => entry.value !== 0);
  if (directNutrients.length > 0) {
    return directNutrients;
  }

  const nutrientEntries = FOOD_MACRONUTRIENT_IDS.map((nutrientId) => ({
    id: nutrientId,
    value: getFiniteNumber(foodLike?.[nutrientId], 0),
  })).concat(
    Object.entries(normalizeMicros(foodLike?.micros)).map(([nutrientId, value]) => ({
      id: nutrientId,
      value: getFiniteNumber(value, 0),
    })),
  );

  return nutrientEntries.filter((entry) => entry.value !== 0);
}

function getFoodNutrientMap(nutrientsPer100g) {
  const nutrientMap = {
    calories: 0,
    carbs: 0,
    protein: 0,
    fats: 0,
    fiber: 0,
    ...createEmptyMicros(),
  };

  normalizeFoodNutrientsPer100g(nutrientsPer100g).forEach((entry) => {
    nutrientMap[entry.id] = entry.value;
  });

  return nutrientMap;
}

function normalizeFoodOrigin(originLike) {
  const originSource = originLike && typeof originLike === "object" ? originLike : {};
  const coachFoodId = normalizeDocId(originSource.coach_food_id);
  const catalogFoodId = normalizeDocId(originSource.catalog_food_id);
  const originIds = [coachFoodId, catalogFoodId].filter(Boolean);

  if (originIds.length !== 1) {
    return null;
  }

  if (coachFoodId) {
    return { coach_food_id: coachFoodId };
  }

  if (catalogFoodId) {
    return { catalog_food_id: catalogFoodId };
  }

  return null;
}

function normalizeRecipeOrigin(originLike) {
  const originSource = originLike && typeof originLike === "object" ? originLike : {};
  const coachRecipeId = normalizeDocId(
    originSource.coach_recipe_id ?? originSource.coachRecipeId,
  );
  const catalogRecipeId = normalizeDocId(
    originSource.catalog_recipe_id ?? originSource.catalogRecipeId,
  );
  const originIds = [coachRecipeId, catalogRecipeId].filter(Boolean);

  if (originIds.length !== 1) {
    return null;
  }

  if (coachRecipeId) {
    return { coach_recipe_id: coachRecipeId };
  }

  return { catalog_recipe_id: catalogRecipeId };
}

function normalizeFoodRecordCommonMeasures(commonMeasures) {
  if (!Array.isArray(commonMeasures)) {
    return [];
  }

  const seenIds = new Set();

  return commonMeasures
    .map((measure) => {
      const measureId = normalizeBuiltInCommonMeasureId(
        measure?.measureKey ?? measure?.id,
      );
      const grams = getFiniteNumber(measure?.grams, 0);

      if (!measureId || grams <= 0 || seenIds.has(measureId)) {
        return null;
      }

      seenIds.add(measureId);

      return {
        id: measureId,
        grams,
      };
    })
    .filter(Boolean);
}

function inflateFoodCommonMeasures(commonMeasures) {
  return normalizeFoodRecordCommonMeasures(commonMeasures).map((measure) => ({
    id: measure.id,
    measureKey: measure.id,
    grams: measure.grams,
  }));
}

function normalizeRecipeMealType(value) {
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  return RECIPE_ALLOWED_MEAL_TYPES.has(normalizedValue) ? normalizedValue : null;
}

function normalizeRecipeMealTypes(recipeLike) {
  const directMealTypes = Array.isArray(recipeLike?.meal_types)
    ? recipeLike.meal_types
    : Array.isArray(recipeLike?.mealTypes)
      ? recipeLike.mealTypes
      : [];
  const normalizedMealTypes = Array.from(
    new Set(directMealTypes.map(normalizeRecipeMealType).filter(Boolean)),
  );

  if (normalizedMealTypes.length > 0) {
    return normalizedMealTypes;
  }

  return [
    RECIPE_MEAL_TYPE_BY_TYPE_KEY[String(recipeLike?.typeKey ?? "").trim()] ?? "main",
  ];
}

function getRecipeTypeKeyFromMealTypes(mealTypes) {
  const normalizedMealTypes = Array.isArray(mealTypes) ? mealTypes : [];
  const primaryMealType = normalizedMealTypes.find((mealType) =>
    RECIPE_ALLOWED_MEAL_TYPES.has(String(mealType ?? "").trim().toLowerCase()),
  );

  return RECIPE_TYPE_KEY_BY_MEAL_TYPE[primaryMealType] ?? "main-string";
}

function formatRuntimeQuantityValue(quantity) {
  const numericValue = Number(quantity);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  const decimalPlaces = numericValue < 10 ? 2 : 1;
  return String(Number(numericValue.toFixed(decimalPlaces)));
}

function getQuantityValueFromGrams(quantityInGrams, selectedMeasureId, commonMeasures) {
  const normalizedQuantityInGrams = getFiniteNumber(quantityInGrams, 0);
  const normalizedMeasureId = String(selectedMeasureId ?? "").trim();

  if (normalizedQuantityInGrams <= 0) {
    return "";
  }

  if (!normalizedMeasureId) {
    return formatRuntimeQuantityValue(normalizedQuantityInGrams);
  }

  const selectedMeasure = inflateFoodCommonMeasures(commonMeasures).find(
    (measure) => measure.id === normalizedMeasureId,
  );
  const gramsPerMeasure = getFiniteNumber(selectedMeasure?.grams, 0);

  if (gramsPerMeasure <= 0) {
    return formatRuntimeQuantityValue(normalizedQuantityInGrams);
  }

  return formatRuntimeQuantityValue(normalizedQuantityInGrams / gramsPerMeasure);
}

function normalizeRecipeFoodSnapshotOrigin(foodLike) {
  const explicitOrigin = normalizeFoodOrigin(foodLike?.origin);

  if (explicitOrigin) {
    return explicitOrigin;
  }

  const coachFoodId = normalizeDocId(foodLike?.foodId);
  const catalogFoodId = normalizeDocId(
    foodLike?.catalogFoodId ?? foodLike?.linkedCatalogFoodId,
  );

  if (coachFoodId) {
    return { coach_food_id: coachFoodId };
  }

  if (catalogFoodId) {
    return { catalog_food_id: catalogFoodId };
  }

  return null;
}

function normalizeRecipeEquivalenceOrigin(originLike) {
  const resolvedOrigin = originLike && typeof originLike === "object" ? originLike : {};
  const coachEquivalentId = normalizeDocId(
    resolvedOrigin?.coach_equivalent_id ?? resolvedOrigin?.coachEquivalentId,
  );
  const catalogEquivalentId = normalizeDocId(
    resolvedOrigin?.catalog_equivalent_id ?? resolvedOrigin?.catalogEquivalentId,
  );

  if (coachEquivalentId && !catalogEquivalentId) {
    return { coach_equivalent_id: coachEquivalentId };
  }

  if (catalogEquivalentId && !coachEquivalentId) {
    return { catalog_equivalent_id: catalogEquivalentId };
  }

  return null;
}

function normalizeSelectedMeasureId(selectedMeasureId, commonMeasures) {
  const normalizedCommonMeasures = inflateFoodCommonMeasures(commonMeasures);

  if (String(selectedMeasureId ?? "").trim() === "grams") {
    return null;
  }

  const normalizedSelectedMeasureId = normalizeBuiltInCommonMeasureId(
    selectedMeasureId,
  );
  if (!normalizedSelectedMeasureId) {
    return null;
  }

  return normalizedCommonMeasures.some(
    (measure) => measure.id === normalizedSelectedMeasureId,
  )
    ? normalizedSelectedMeasureId
    : null;
}

function normalizeIngredient(ingredient) {
  const source = normalizeIngredientSource(ingredient);
  const foodId =
    source === "coach_created" ? normalizeDocId(ingredient?.foodId ?? ingredient?.id) : null;
  const catalogFoodId =
    isCatalogRuntimeSource(source)
      ? normalizeDocId(
          ingredient?.catalogFoodId ?? ingredient?.linkedCatalogFoodId ?? ingredient?.id,
        )
      : null;
  const classification = normalizeFoodClassification(ingredient);

  return {
    id: normalizeDocId(ingredient?.id, ingredient?.stableKey ?? "ingredient"),
    stableKey: String(
      ingredient?.stableKey ??
        ingredient?.foodId ??
        ingredient?.catalogFoodId ??
        ingredient?.id ??
        ingredient?.name ??
        "ingredient",
    ),
    name: String(ingredient?.name ?? ""),
    quantity: getFiniteNumber(ingredient?.quantity, 0),
    calories: getFiniteNumber(ingredient?.calories, 0),
    carbs: getFiniteNumber(ingredient?.carbs, 0),
    protein: getFiniteNumber(ingredient?.protein, 0),
    fats: getFiniteNumber(ingredient?.fats, 0),
    fiber: getFiniteNumber(ingredient?.fiber, 0),
    source,
    sourceLabel: String(ingredient?.sourceLabel ?? ""),
    databaseKey: String(
      ingredient?.databaseKey ??
        (isCatalogRuntimeSource(source)
          ? "catalog"
          : source === "demo"
            ? "demo"
            : source === "legacy_snapshot"
              ? "legacy_snapshot"
              : "createdByMe"),
    ),
    foodId,
    catalogFoodId,
    brand: hasText(ingredient?.brand) ? String(ingredient.brand).trim() : null,
    store: hasText(ingredient?.store) ? String(ingredient.store).trim() : null,
    allergen_codes: classification.allergen_codes,
    allergen_data_complete: classification.allergen_data_complete,
    is_vegetarian: classification.is_vegetarian,
    is_vegan: classification.is_vegan,
    dietary_data_complete: classification.dietary_data_complete,
    allergens: classification.allergen_codes,
    allergenDataComplete: classification.allergen_data_complete,
    isVegetarian: classification.is_vegetarian,
    isVegan: classification.is_vegan,
    dietaryDataComplete: classification.dietary_data_complete,
    micros: normalizeMicros(ingredient?.micros),
    commonMeasures: normalizeCommonMeasures(
      ingredient?.commonMeasures ?? ingredient?.common_measures,
    ),
    common_measures: normalizeCommonMeasures(
      ingredient?.commonMeasures ?? ingredient?.common_measures,
    ),
    equivalences: Array.isArray(ingredient?.equivalences)
      ? ingredient.equivalences.map(normalizeIngredient)
      : [],
  };
}

function buildRuntimeSourceMetadata(foodLike, origin) {
  const customLabel = [foodLike?.brand, foodLike?.store]
    .filter(hasText)
    .map((value) => String(value).trim())
    .join("  •  ");

  if (origin?.catalog_food_id) {
    const catalogSource = getPersistedCatalogSource(
      foodLike?.source ?? foodLike?.catalogSource,
    );

    return {
      source: catalogSource,
      sourceLabel: getCatalogSourceLabelFromSnapshot(catalogSource),
      databaseKey: "catalog",
      foodId: null,
      catalogFoodId: origin.catalog_food_id,
    };
  }

  if (origin?.coach_food_id) {
    return {
      source: "coach_created",
      sourceLabel: customLabel || "Created by me",
      databaseKey: "createdByMe",
      foodId: origin.coach_food_id,
      catalogFoodId: null,
    };
  }

  return {
    source: "coach_created",
    sourceLabel: customLabel || "Created by me",
    databaseKey: "createdByMe",
    foodId: null,
    catalogFoodId: null,
  };
}

function buildRecipeFoodSnapshot(foodLike, options = {}) {
  const classification = normalizeFoodClassification(foodLike);
  const commonMeasures = normalizeFoodRecordCommonMeasures(
    foodLike?.commonMeasures ?? foodLike?.common_measures,
  );
  const selectedMeasureId = normalizeSelectedMeasureId(
    foodLike?.selected_measure_id ??
      foodLike?.selectedMeasureId ??
      foodLike?.quantityUnitKey,
    commonMeasures,
  );
  const origin = normalizeRecipeFoodSnapshotOrigin(foodLike);
  const foodSnapshot = {
    id: normalizeDocId(
      foodLike?.id,
      options.fallbackId ?? foodLike?.stableKey ?? foodLike?.name ?? "item",
    ),
    type: "food",
    name: String(foodLike?.name ?? "").trim(),
    quantity_g: getFiniteNumber(foodLike?.quantity_g ?? foodLike?.quantity, 0),
    nutrients_per_100g: buildFoodNutrientsPer100g(foodLike),
    common_measures: commonMeasures,
    allergen_codes: classification.allergen_codes,
    allergen_data_complete: classification.allergen_data_complete,
    is_vegetarian: classification.is_vegetarian,
    is_vegan: classification.is_vegan,
    dietary_data_complete: classification.dietary_data_complete,
  };

  if (selectedMeasureId) {
    foodSnapshot.selected_measure_id = selectedMeasureId;
  }

  if (origin) {
    foodSnapshot.origin = origin;
  }

  if (origin?.catalog_food_id) {
    foodSnapshot.source = getPersistedCatalogSource(
      foodLike?.source ?? foodLike?.catalogSource,
    );
  }

  if (hasText(foodLike?.brand)) {
    foodSnapshot.brand = String(foodLike.brand).trim();
  }

  if (hasText(foodLike?.store)) {
    foodSnapshot.store = String(foodLike.store).trim();
  }

  const normalizedWebsite = normalizeNutritionWebsiteUrl(foodLike?.website);
  if (normalizedWebsite) {
    foodSnapshot.website = normalizedWebsite;
  }

  return foodSnapshot;
}

function buildRecipeEquivalenceOptionSnapshot(optionLike, options = {}) {
  const foodSnapshot = buildRecipeFoodSnapshot(optionLike, options);
  const { type, ...optionSnapshot } = foodSnapshot;

  if (optionSnapshot.origin?.catalog_food_id) {
    optionSnapshot.source = getPersistedCatalogSource(
      optionLike?.catalogSource ?? optionLike?.source,
    );
  }

  return optionSnapshot;
}

function normalizeRecipeFoodItem(itemLike, options = {}) {
  const foodSnapshot = buildRecipeFoodSnapshot(itemLike, options);
  return {
    ...foodSnapshot,
    type: "food",
  };
}

function normalizeRecipeEquivalenceItem(itemLike, index = 0) {
  const options = (Array.isArray(itemLike?.options) ? itemLike.options : [])
    .map((option, optionIndex) =>
      buildRecipeEquivalenceOptionSnapshot(option, {
        fallbackId: `${itemLike?.id ?? "equivalence"}-option-${optionIndex}`,
      }),
    )
    .filter((option) => option.name);
  const origin = normalizeRecipeEquivalenceOrigin(itemLike?.origin);

  if (options.length === 0) {
    return null;
  }

  const equivalenceItem = {
    id: normalizeDocId(itemLike?.id, `equivalence-${index}`),
    type: "equivalence",
    name: String(itemLike?.name ?? "").trim() || options[0].name,
    options,
  };

  if (origin) {
    equivalenceItem.origin = origin;
  }

  return equivalenceItem;
}

export function buildNutritionFoodItemSnapshot(foodLike, options = {}) {
  return normalizeRecipeFoodItem(foodLike, options);
}

export function buildNutritionEquivalenceItemSnapshot(itemLike, index = 0) {
  return normalizeRecipeEquivalenceItem(itemLike, index);
}

function buildRecipeItemsFromIngredients(ingredients) {
  return (Array.isArray(ingredients) ? ingredients : []).reduce((items, ingredient, index) => {
    const equivalenceOptions = Array.isArray(ingredient?.equivalences)
      ? ingredient.equivalences
      : [];

    if (equivalenceOptions.length > 0) {
      const equivalenceItem = buildNutritionEquivalenceItemSnapshot(
        {
          id:
            ingredient?.equivalenceGroupId ??
            ingredient?.equivalence_group_id ??
            `${ingredient?.id ?? `item-${index}`}-equivalence`,
          name: ingredient?.equivalenceName ?? ingredient?.name,
          options: [ingredient, ...equivalenceOptions],
          origin: ingredient?.equivalenceOrigin ?? ingredient?.equivalence_origin,
        },
        index,
      );

      if (equivalenceItem) {
        items.push(equivalenceItem);
      }

      return items;
    }

    const foodItem = buildNutritionFoodItemSnapshot(ingredient, {
      fallbackId: `item-${index}`,
    });

    if (foodItem.name) {
      items.push(foodItem);
    }

    return items;
  }, []);
}

function normalizeRecipeItems(itemsLike) {
  return (Array.isArray(itemsLike) ? itemsLike : [])
    .map((item, index) => {
      if (item?.type === "equivalence") {
        return buildNutritionEquivalenceItemSnapshot(item, index);
      }

      return buildNutritionFoodItemSnapshot(item, {
        fallbackId: `item-${index}`,
      });
    })
    .filter((item) => item?.name);
}

function scaleNutrientsForQuantity(nutrientsPer100g, quantityInGrams) {
  const nutrientMap = getFoodNutrientMap(nutrientsPer100g);
  const factor = getFiniteNumber(quantityInGrams, 0) > 0 ? quantityInGrams / 100 : 0;
  const scaledMicros = createEmptyMicros();

  NUTRITION_MICRO_KEYS.forEach((key) => {
    scaledMicros[key] = getFiniteNumber(nutrientMap[key], 0) * factor;
  });

  return {
    calories: getFiniteNumber(nutrientMap.calories, 0) * factor,
    carbs: getFiniteNumber(nutrientMap.carbs, 0) * factor,
    protein: getFiniteNumber(nutrientMap.protein, 0) * factor,
    fats: getFiniteNumber(nutrientMap.fats, 0) * factor,
    fiber: getFiniteNumber(nutrientMap.fiber, 0) * factor,
    micros: scaledMicros,
  };
}

function buildRuntimeIngredientFromRecipeFoodItem(itemLike) {
  const commonMeasures = inflateFoodCommonMeasures(itemLike?.common_measures);
  const nutrientsPer100g = normalizeFoodNutrientsPer100g(itemLike?.nutrients_per_100g);
  const nutrientMap = getFoodNutrientMap(nutrientsPer100g);
  const origin = normalizeRecipeFoodSnapshotOrigin(itemLike);
  const selectedMeasureId = normalizeSelectedMeasureId(
    itemLike?.selected_measure_id,
    commonMeasures,
  );
  const runtimeSource = buildRuntimeSourceMetadata(itemLike, origin);
  const quantityInGrams = getFiniteNumber(itemLike?.quantity_g, 0);
  const quantityValue = selectedMeasureId
    ? getQuantityValueFromGrams(quantityInGrams, selectedMeasureId, commonMeasures)
    : formatRuntimeQuantityValue(quantityInGrams);
  const classification = normalizeFoodClassification(itemLike);

  return {
    id: normalizeDocId(itemLike?.id, itemLike?.name ?? "ingredient"),
    stableKey: String(
      origin?.coach_food_id ??
        origin?.catalog_food_id ??
        itemLike?.id ??
        itemLike?.name ??
        "ingredient",
    ),
    name: String(itemLike?.name ?? ""),
    quantity: quantityValue,
    quantity_g: quantityInGrams,
    quantityUnitKey: selectedMeasureId ?? "grams",
    selected_measure_id: selectedMeasureId,
    calories: getFiniteNumber(nutrientMap.calories, 0),
    carbs: getFiniteNumber(nutrientMap.carbs, 0),
    protein: getFiniteNumber(nutrientMap.protein, 0),
    fats: getFiniteNumber(nutrientMap.fats, 0),
    fiber: getFiniteNumber(nutrientMap.fiber, 0),
    micros: normalizeMicros(nutrientMap),
    nutrients_per_100g: nutrientsPer100g,
    commonMeasures,
    common_measures: commonMeasures,
    origin,
    brand: hasText(itemLike?.brand) ? String(itemLike.brand).trim() : null,
    store: hasText(itemLike?.store) ? String(itemLike.store).trim() : null,
    website: normalizeNutritionWebsiteUrl(itemLike?.website),
    ...runtimeSource,
    allergen_codes: classification.allergen_codes,
    allergen_data_complete: classification.allergen_data_complete,
    is_vegetarian: classification.is_vegetarian,
    is_vegan: classification.is_vegan,
    dietary_data_complete: classification.dietary_data_complete,
    allergens: classification.allergen_codes,
    allergenDataComplete: classification.allergen_data_complete,
    isVegetarian: classification.is_vegetarian,
    isVegan: classification.is_vegan,
    dietaryDataComplete: classification.dietary_data_complete,
    equivalences: [],
  };
}

function buildLinkedRuntimeEquivalentFromRecipeFoodItem(itemLike) {
  const runtimeIngredient = buildRuntimeIngredientFromRecipeFoodItem(itemLike);

  return {
    ...runtimeIngredient,
    quantity: runtimeIngredient.quantity_g <= 0 ? "0" : runtimeIngredient.quantity,
    isAutoCalculated: true,
  };
}

function sortByOrder(items) {
  return [...(Array.isArray(items) ? items : [])].sort((leftItem, rightItem) => {
    const leftOrder = getFiniteNumber(leftItem?.order, 0);
    const rightOrder = getFiniteNumber(rightItem?.order, 0);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return String(leftItem?.name ?? "").localeCompare(String(rightItem?.name ?? ""), undefined, {
      sensitivity: "base",
    });
  });
}

function sortByName(items) {
  return [...(Array.isArray(items) ? items : [])].sort((leftItem, rightItem) =>
    String(leftItem?.name ?? "").localeCompare(String(rightItem?.name ?? ""), undefined, {
      sensitivity: "base",
    }),
  );
}

function getMealOptionTotals(optionLike) {
  return (Array.isArray(optionLike?.items) ? optionLike.items : []).reduce(
    (totals, item) => addTotals(totals, createNutritionItem(item)?.totals),
    createEmptyTotals(),
  );
}

function normalizeClientPlanSummaryTotals(summaryTotals) {
  return {
    calories: getFiniteNumber(summaryTotals?.calories, 0),
    carbs: getFiniteNumber(summaryTotals?.carbs, 0),
    protein: getFiniteNumber(summaryTotals?.protein, 0),
    fats: getFiniteNumber(summaryTotals?.fats, 0),
    fiber: getFiniteNumber(summaryTotals?.fiber, 0),
  };
}

function buildClientPlanSummaryTotals(meals, fallbackSummaryTotals = null) {
  const normalizedMeals = Array.isArray(meals) ? meals : [];
  if (normalizedMeals.length === 0) {
    return normalizeClientPlanSummaryTotals(fallbackSummaryTotals);
  }

  const totals = normalizedMeals.reduce((mealTotals, meal) => {
    const defaultOption = Array.isArray(meal?.options) ? meal.options[0] : null;
    return addTotals(mealTotals, getMealOptionTotals(defaultOption));
  }, createEmptyTotals());

  return normalizeClientPlanSummaryTotals(totals);
}

function normalizeClientPlanGoalEntries(entries, { allowedIds = null } = {}) {
  const normalizedEntries = Array.isArray(entries)
    ? entries.map((entry) => ({
        id: String(entry?.id ?? "").trim(),
        value: getFiniteNumber(entry?.value, 0),
      }))
    : entries && typeof entries === "object"
      ? Object.keys(entries).map((id) => ({
          id: String(id ?? "").trim(),
          value: getFiniteNumber(entries[id], 0),
        }))
      : [];

  return normalizedEntries
    .filter((entry) => {
      if (!entry.id) {
        return false;
      }

      return allowedIds == null || allowedIds.has(entry.id);
    });
}

function normalizeClientPlanGoals(goals) {
  const normalizedGoals = goals && typeof goals === "object" ? goals : null;
  if (!normalizedGoals) {
    return null;
  }

  const macroInputMode = String(
    normalizedGoals?.macro_input_mode ?? normalizedGoals?.macroInputMode ?? "grams",
  )
    .trim()
    .toLowerCase();
  const nutrients = normalizeClientPlanGoalEntries(normalizedGoals?.nutrients);
  const percentages = normalizeClientPlanGoalEntries(normalizedGoals?.percentages, {
    allowedIds: new Set(["protein", "carbs", "fats"]),
  });

  if (nutrients.length === 0 && percentages.length === 0) {
    return macroInputMode === "grams"
      ? {
          macro_input_mode: "grams",
          nutrients: [],
        }
      : null;
  }

  const record = {
    macro_input_mode: macroInputMode === "percent" ? "percent" : "grams",
    nutrients,
  };

  if (percentages.length > 0) {
    record.percentages = percentages;
  }

  return record;
}

function normalizeGoalRecord(goals) {
  if (!goals || typeof goals !== "object") {
    return null;
  }

  const nutrients = normalizeClientPlanGoalEntries(goals?.nutrients).filter(
    (entry) => entry.value > 0,
  );
  const percentages = normalizeClientPlanGoalEntries(goals?.percentages).filter(
    (entry) => entry.value > 0,
  );
  const macroInputMode = String(goals?.macro_input_mode ?? "").trim() || "percent";

  if (nutrients.length === 0 && percentages.length === 0) {
    return macroInputMode === "grams"
      ? {
          macro_input_mode: "grams",
          nutrients: [],
        }
      : null;
  }

  const record = {
    macro_input_mode: macroInputMode,
    nutrients,
  };

  if (percentages.length > 0) {
    record.percentages = percentages;
  }

  return record;
}

export function buildClientNutritionPlanMealRecord(mealLike, options = {}) {
  const allowEmptyOptions = options?.allowEmptyOptions === true;
  const normalizedOptions = (Array.isArray(mealLike?.options) ? mealLike.options : [])
    .map((option, optionIndex) => {
      const optionId = normalizeDocId(option?.id, `option-${optionIndex}`);
      const optionName = String(option?.name ?? "").trim();
      const normalizedItems = (Array.isArray(option?.items) ? option.items : [])
        .map((item) => JSON.parse(JSON.stringify(item)))
        .filter((item) => item && typeof item === "object");

      if (
        !optionId ||
        !optionName ||
        (!allowEmptyOptions && normalizedItems.length === 0)
      ) {
        return null;
      }

      return {
        id: optionId,
        name: optionName,
        items: normalizedItems,
      };
    })
    .filter(Boolean);

  const record = {
    id: normalizeDocId(mealLike?.id, mealLike?.name ?? "meal"),
    name: String(mealLike?.name ?? "").trim(),
    order: getFiniteNumber(mealLike?.order, 0),
    options: normalizedOptions,
  };

  if (hasText(mealLike?.notes)) {
    record.notes = String(mealLike.notes).trim();
  }

  if (hasText(mealLike?.day_id)) {
    record.day_id = String(mealLike.day_id).trim();
  }

  if (hasText(mealLike?.day_name)) {
    record.day_name = String(mealLike.day_name).trim();
  }

  return record;
}

export function buildClientEditablePlanRootRecord(
  planData = {},
  mealRecords = [],
  options = {},
) {
  const normalizedId = normalizePlanId(planData?.id, options.planId ?? "plan");
  const normalizedStatus = String(
    options.status ?? planData?.status ?? CLIENT_NUTRITION_STATUS_ACTIVE,
  ).trim();
  const normalizedPurpose = String(
    options.purpose ?? planData?.purpose ?? CLIENT_NUTRITION_DEFAULT_PURPOSE,
  ).trim() || CLIENT_NUTRITION_DEFAULT_PURPOSE;
  const normalizedOriginType = String(
    options.originType ?? planData?.origin_type ?? "client_direct",
  ).trim() || "client_direct";
  const normalizedGoals = normalizeGoalRecord(options.goals ?? planData?.goals);

  const record = {
    schema_version: CLIENT_EDITABLE_PLAN_SCHEMA_VERSION,
    record_kind: CLIENT_EDITABLE_PLAN_RECORD_KIND,
    id: normalizedId,
    name: String(planData?.name ?? "").trim(),
    _rev: getFiniteNumber(options.rev ?? planData?._rev, 0),
    meal_count: Array.isArray(mealRecords) ? mealRecords.length : 0,
    summary_totals: buildClientPlanSummaryTotals(mealRecords, planData?.summary_totals),
    status:
      normalizedStatus === CLIENT_NUTRITION_STATUS_ARCHIVED
        ? CLIENT_NUTRITION_STATUS_ARCHIVED
        : CLIENT_NUTRITION_STATUS_ACTIVE,
    purpose: normalizedPurpose,
    origin_type: normalizedOriginType,
  };

  if (hasText(planData?.notes)) {
    record.notes = String(planData.notes).trim();
  }

  if (normalizedGoals) {
    record.goals = normalizedGoals;
  }

  if (options.createdAt !== undefined) {
    record.createdAt = options.createdAt;
  } else if (planData?.createdAt != null) {
    record.createdAt = planData.createdAt;
  }

  if (options.updatedAt !== undefined) {
    record.updatedAt = options.updatedAt;
  } else if (planData?.updatedAt != null) {
    record.updatedAt = planData.updatedAt;
  }

  if (options.activatedAt !== undefined) {
    if (options.activatedAt != null) {
      record.activated_at = options.activatedAt;
    }
  } else if (planData?.activated_at != null) {
    record.activated_at = planData.activated_at;
  }

  if (options.deactivatedAt !== undefined) {
    if (options.deactivatedAt != null) {
      record.deactivated_at = options.deactivatedAt;
    }
  } else if (planData?.deactivated_at != null) {
    record.deactivated_at = planData.deactivated_at;
  }

  const normalizedOriginPlanId = normalizePlanId(
    options.originPlanId ?? planData?.origin_plan_id,
    "",
  );
  if (normalizedOriginPlanId) {
    record.origin_plan_id = normalizedOriginPlanId;
  }

  const originPlanRev = getFiniteNumber(
    options.originPlanRev ?? planData?.origin_plan_rev,
    null,
  );
  if (originPlanRev != null) {
    record.origin_plan_rev = originPlanRev;
  }

  assertNutritionLimit(
    validatePlanDraftLimits({
      name: record.name,
      notes: record.notes,
      meals: mealRecords,
    }),
  );

  return record;
}

export function buildClientNutritionPlanSnapshot(planLike, options = {}) {
  const clientPlanId = normalizeDocId(options.clientPlanId, planLike?.id ?? "client-plan");
  const mealRecords = sortByOrder(planLike?.meals).map((meal) =>
    buildClientNutritionPlanMealRecord(meal),
  );
  const goals = normalizeClientPlanGoals(options.goals ?? planLike?.goals);

  return {
    rootRecord: buildClientEditablePlanRootRecord(planLike, mealRecords, {
      planId: clientPlanId,
      rev: options.rev ?? planLike?._rev,
      goals,
      activatedAt: options.activatedAt ?? planLike?.activated_at ?? null,
      createdAt: options.createdAt ?? planLike?.createdAt ?? null,
      updatedAt: options.updatedAt ?? planLike?.updatedAt ?? null,
      status:
        options.status === CLIENT_NUTRITION_STATUS_ARCHIVED
          ? CLIENT_NUTRITION_STATUS_ARCHIVED
          : String(planLike?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
            ? CLIENT_NUTRITION_STATUS_ARCHIVED
            : CLIENT_NUTRITION_STATUS_ACTIVE,
      purpose:
        String(options.purpose ?? planLike?.purpose ?? CLIENT_NUTRITION_DEFAULT_PURPOSE).trim() ||
        CLIENT_NUTRITION_DEFAULT_PURPOSE,
      originType:
        String(options.originType ?? planLike?.origin_type ?? "client_direct").trim() ||
        "client_direct",
      deactivatedAt: options.deactivatedAt ?? planLike?.deactivated_at ?? null,
      originPlanId: options.originPlanId ?? planLike?.origin_plan_id,
      originPlanRev: options.originPlanRev ?? planLike?.origin_plan_rev,
    }),
    mealRecords,
  };
}

export function sortClientNutritionPlans(plans) {
  return [...(Array.isArray(plans) ? plans : [])].sort((leftPlan, rightPlan) => {
    const leftIsArchived =
      String(leftPlan?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED;
    const rightIsArchived =
      String(rightPlan?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED;

    if (leftIsArchived !== rightIsArchived) {
      return leftIsArchived ? 1 : -1;
    }

    const leftDate = new Date(
      leftPlan?.updatedAt?.toDate?.() ??
        leftPlan?.updatedAt ??
        leftPlan?.activated_at?.toDate?.() ??
        leftPlan?.activated_at ??
        leftPlan?.createdAt?.toDate?.() ??
        leftPlan?.createdAt ??
        leftPlan?.deployedAt?.toDate?.() ??
        leftPlan?.deployedAt ??
        0,
    ).getTime();
    const rightDate = new Date(
      rightPlan?.updatedAt?.toDate?.() ??
        rightPlan?.updatedAt ??
        rightPlan?.activated_at?.toDate?.() ??
        rightPlan?.activated_at ??
        rightPlan?.createdAt?.toDate?.() ??
        rightPlan?.createdAt ??
        rightPlan?.deployedAt?.toDate?.() ??
        rightPlan?.deployedAt ??
        0,
    ).getTime();

    if (rightDate !== leftDate) {
      return rightDate - leftDate;
    }

    return String(leftPlan?.name ?? "").localeCompare(String(rightPlan?.name ?? ""), undefined, {
      sensitivity: "base",
    });
  });
}

export function buildNutritionFoodRecord(foodLike, options = {}) {
  const normalizedId = normalizeDocId(foodLike?.id, foodLike?.code ?? "food");
  const classification = normalizeFoodClassification(foodLike);
  const origin = normalizeFoodOrigin(foodLike?.origin);
  const commonMeasures = normalizeFoodRecordCommonMeasures(
    foodLike?.commonMeasures ?? foodLike?.common_measures,
  );
  const selectedMeasureId = normalizeSelectedMeasureId(
    foodLike?.selected_measure_id ??
      foodLike?.selectedMeasureId ??
      foodLike?.quantityUnitKey,
    commonMeasures,
  );

  const record = {
    schema_version: FOOD_SCHEMA_VERSION,
    id: normalizedId,
    name: String(foodLike?.name ?? "").trim(),
    type: foodLike?.type === "supplement" ? "supplement" : "food",
    reference_quantity_g: getFiniteNumber(
      foodLike?.quantityReferenceG ?? foodLike?.reference_quantity_g ?? foodLike?.quantity,
      100,
    ),
    nutrients_per_100g: buildFoodNutrientsPer100g(foodLike),
    common_measures: commonMeasures,
    allergen_codes: classification.allergen_codes,
    allergen_data_complete: classification.allergen_data_complete,
    is_vegetarian: classification.is_vegetarian,
    is_vegan: classification.is_vegan,
    dietary_data_complete: classification.dietary_data_complete,
  };

  if (selectedMeasureId) {
    record.selected_measure_id = selectedMeasureId;
  }

  const normalizedWebsite = normalizeNutritionWebsiteUrl(foodLike?.website);
  if (normalizedWebsite) {
    record.website = normalizedWebsite;
  }

  if (hasText(foodLike?.brand)) {
    record.brand = String(foodLike.brand).trim();
  }

  if (hasText(foodLike?.store)) {
    record.store = String(foodLike.store).trim();
  }

  if (origin) {
    record.origin = origin;
  }

  const revision = getFiniteNumber(options.rev ?? foodLike?._rev, null);
  if (revision != null) {
    record._rev = revision;
  }

  if (options.createdAt !== undefined) {
    record.createdAt = options.createdAt;
  }

  if (options.updatedAt !== undefined) {
    record.updatedAt = options.updatedAt;
  }

  return record;
}

export function inflateNutritionFoodRecord(foodRecord, options = {}) {
  const nutrientsPer100g = normalizeFoodNutrientsPer100g(foodRecord?.nutrients_per_100g);
  const nutrientMap = getFoodNutrientMap(nutrientsPer100g);
  const normalizedCommonMeasures = inflateFoodCommonMeasures(foodRecord?.common_measures);
  const runtimeClassification = getRuntimeFoodClassification(foodRecord);
  const origin = normalizeFoodOrigin(foodRecord?.origin);
  const selectedMeasureId = normalizeSelectedMeasureId(
    foodRecord?.selected_measure_id ?? foodRecord?.selectedMeasureId,
    normalizedCommonMeasures,
  );
  const referenceQuantityInGrams = getFiniteNumber(foodRecord?.reference_quantity_g, 100);
  const quantityValue = selectedMeasureId
    ? getQuantityValueFromGrams(
        referenceQuantityInGrams,
        selectedMeasureId,
        normalizedCommonMeasures,
      )
    : referenceQuantityInGrams;

  return {
    id: normalizeDocId(foodRecord?.id),
    name: String(foodRecord?.name ?? ""),
    type: foodRecord?.type === "supplement" ? "supplement" : "food",
    quantity: quantityValue,
    quantityReferenceG: referenceQuantityInGrams,
    reference_quantity_g: referenceQuantityInGrams,
    quantityUnitKey: selectedMeasureId ?? "grams",
    selected_measure_id: selectedMeasureId,
    nutrients_per_100g: nutrientsPer100g,
    calories: getFiniteNumber(nutrientMap.calories, 0),
    carbs: getFiniteNumber(nutrientMap.carbs, 0),
    protein: getFiniteNumber(nutrientMap.protein, 0),
    fats: getFiniteNumber(nutrientMap.fats, 0),
    fiber: getFiniteNumber(nutrientMap.fiber, 0),
    micros: normalizeMicros(nutrientMap),
    commonMeasures: normalizedCommonMeasures,
    common_measures: normalizedCommonMeasures,
    website: normalizeNutritionWebsiteUrl(foodRecord?.website),
    brand: hasText(foodRecord?.brand) ? String(foodRecord.brand).trim() : null,
    store: hasText(foodRecord?.store) ? String(foodRecord.store).trim() : null,
    origin,
    ...runtimeClassification,
    coach: options.coach ?? null,
    createdByMe: true,
    _rev: getFiniteNumber(foodRecord?._rev, 0),
    _nutrition: true,
    createdAt: foodRecord?.createdAt ?? null,
    updatedAt: foodRecord?.updatedAt ?? null,
  };
}

export function buildNutritionRecipeRecord(recipeLike, options = {}) {
  const mealTypes = normalizeRecipeMealTypes(recipeLike);
  const items =
    Array.isArray(recipeLike?.items) && recipeLike.items.length > 0
      ? normalizeRecipeItems(recipeLike.items)
      : buildRecipeItemsFromIngredients(recipeLike?.ingredients);
  const origin = normalizeRecipeOrigin(recipeLike?.origin);
  const prepTimeMinutes = getFiniteNumber(
    recipeLike?.preparationTime ??
      recipeLike?.preparation_time_min ??
      recipeLike?.prep_time_min,
    null,
  );
  const imageUri = hasText(recipeLike?.imageUri ?? recipeLike?.image_uri)
    ? String(recipeLike?.imageUri ?? recipeLike?.image_uri).trim()
    : "";

  const record = {
    schema_version: RECIPE_SCHEMA_VERSION,
    id: normalizeDocId(recipeLike?.id, recipeLike?.name ?? "recipe"),
    name: String(recipeLike?.name ?? "").trim(),
    order: getFiniteNumber(recipeLike?.order, 0),
    meal_types: mealTypes,
    items,
  };

  if (hasText(recipeLike?.description)) {
    record.description = String(recipeLike.description).trim();
  }

  if (prepTimeMinutes != null && prepTimeMinutes >= 0) {
    record.prep_time_min = prepTimeMinutes;
  }

  if (imageUri) {
    record.image_uri = imageUri;
  }

  if (origin) {
    record.origin = origin;
  }

  const revision = getFiniteNumber(options.rev ?? recipeLike?._rev, null);
  if (revision != null) {
    record._rev = revision;
  }

  if (options.createdAt !== undefined) {
    record.createdAt = options.createdAt;
  }

  if (options.updatedAt !== undefined) {
    record.updatedAt = options.updatedAt;
  }

  return record;
}

export function inflateNutritionRecipeRecord(recipeRecord) {
  const items = normalizeRecipeItems(recipeRecord?.items);
  const runtimeIngredients = [];

  items.forEach((item) => {
    if (item?.type === "equivalence") {
      const [primaryOption, ...alternativeOptions] = Array.isArray(item?.options)
        ? item.options
        : [];

      if (!primaryOption) {
        return;
      }

      const runtimeIngredient = buildRuntimeIngredientFromRecipeFoodItem(primaryOption);
      runtimeIngredient.equivalenceGroupId = item.id;
      runtimeIngredient.equivalenceName = item.name;
      runtimeIngredient.equivalenceOrigin = normalizeRecipeEquivalenceOrigin(item?.origin);
      runtimeIngredient.equivalences = alternativeOptions.map((option) =>
        buildLinkedRuntimeEquivalentFromRecipeFoodItem(option),
      );
      runtimeIngredients.push(runtimeIngredient);
      return;
    }

    runtimeIngredients.push(buildRuntimeIngredientFromRecipeFoodItem(item));
  });

  const recipeTotals = runtimeIngredients.reduce(
    (totals, ingredient) => {
      const itemTotals = scaleNutrientsForQuantity(
        ingredient?.nutrients_per_100g,
        ingredient?.quantity_g,
      );

      totals.calories += itemTotals.calories;
      totals.carbs += itemTotals.carbs;
      totals.protein += itemTotals.protein;
      totals.fats += itemTotals.fats;
      totals.fiber += itemTotals.fiber;
      NUTRITION_MICRO_KEYS.forEach((key) => {
        totals.micros[key] += itemTotals.micros[key];
      });

      return totals;
    },
    {
      calories: 0,
      carbs: 0,
      protein: 0,
      fats: 0,
      fiber: 0,
      micros: createEmptyMicros(),
    },
  );
  const recipeClassification = normalizeFoodClassification({
    ...deriveRecipeClassification(runtimeIngredients),
  });

  const mealTypes = normalizeRecipeMealTypes(recipeRecord);
  const origin = normalizeRecipeOrigin(recipeRecord?.origin);

  return {
    id: normalizeDocId(recipeRecord?.id),
    name: String(recipeRecord?.name ?? ""),
    order: getFiniteNumber(recipeRecord?.order, 0),
    description: hasText(recipeRecord?.description)
      ? String(recipeRecord.description).trim()
      : "",
    preparationTime:
      recipeRecord?.prep_time_min != null
        ? String(getFiniteNumber(recipeRecord?.prep_time_min, 0))
        : "",
    imageUri: hasText(recipeRecord?.image_uri) ? String(recipeRecord.image_uri).trim() : null,
    meal_types: mealTypes,
    typeKey: getRecipeTypeKeyFromMealTypes(mealTypes),
    createdByMe: true,
    calories: getFiniteNumber(recipeTotals.calories, 0),
    carbs: getFiniteNumber(recipeTotals.carbs, 0),
    protein: getFiniteNumber(recipeTotals.protein, 0),
    fats: getFiniteNumber(recipeTotals.fats, 0),
    fiber: getFiniteNumber(recipeTotals.fiber, 0),
    micros: normalizeMicros(recipeTotals.micros),
    foods: runtimeIngredients.map((ingredient) => ingredient.name),
    allergens: recipeClassification.allergen_codes,
    allergenDataComplete: recipeClassification.allergen_data_complete,
    isVegetarian: recipeClassification.is_vegetarian,
    isVegan: recipeClassification.is_vegan,
    dietaryDataComplete: recipeClassification.dietary_data_complete,
    items,
    ingredients: runtimeIngredients,
    origin,
    schema_version: recipeRecord?.schema_version ?? RECIPE_SCHEMA_VERSION,
    _rev: getFiniteNumber(recipeRecord?._rev, 0),
    _nutrition: true,
    createdAt: recipeRecord?.createdAt ?? null,
    updatedAt: recipeRecord?.updatedAt ?? null,
  };
}

export function sortNutritionFoods(foods) {
  return sortByName(foods);
}

export function sortNutritionRecipes(recipes) {
  return sortByOrder(recipes);
}
